import { MAX_STORED_MARKING_HTML_LENGTH } from '~/constants/markings'
import { isInlineLawReference } from './text-filter'
import {
  filterSemanticMarkingClasses,
  hasRemovedMarkingClass,
  isSafeMarkingImageSrc,
  MARKING_CAPTURE_UNWRAP_TAGS,
  MARKING_EXCLUDED_ATTRIBUTE,
  MARKING_REMOVED_TAGS,
  MARKING_STORED_TAGS,
} from '#shared/utils/markings/rules'

/**
 * Serializes a Range into an HTML fragment for storage on a marking,
 * preserving the semantic backbone (headings, paragraphs, lists, classes)
 * while stripping dangerous tags, scaffolding and non-class attributes.
 *
 * This is capture-time cleanup, NOT the security boundary: the policy in
 * #shared/utils/markings/rules is enforced again server-side on write
 * (sanitize-marking-html.ts) and by DOMPurify at render time
 * (MarkingDetail.vue), so bypassing this code can't store or render more.
 */

const REMOVED_TAGS = new Set(MARKING_REMOVED_TAGS)

function isExcludedFromMarkings(el: Element): boolean {
  if (el.hasAttribute(MARKING_EXCLUDED_ATTRIBUTE)) {
    return true
  }

  return !isInlineLawReference(el) && hasRemovedMarkingClass(el.getAttribute('class'))
}

/**
 * Law content encodes footnote references as empty `<sup></sup>` — strip
 * those; typography sup (m<sup>3</sup>) has content and survives.
 */
function removeEmptySups(fragment: DocumentFragment): void {
  const sups = Array.from(fragment.querySelectorAll('sup'))

  for (const sup of sups) {
    if (!sup.textContent?.trim()) {
      sup.remove()
    }
  }
}

const UNWRAP_TAGS = new Set(MARKING_CAPTURE_UNWRAP_TAGS)

const STORED_TAGS = new Set(MARKING_STORED_TAGS)

function isStrippableAttr(tag: string, name: string): boolean {
  const lower = name.toLowerCase()

  if (tag === 'img' && (lower === 'src' || lower === 'alt')) {
    return false
  }

  return lower !== 'class'
}

/**
 * Absolutize `src` (relative paths 404 on the workspace page); drop the
 * element when the resolved URL isn't plain http(s).
 */
function sanitizeImage(el: Element): void {
  const src = el.getAttribute('src')

  if (!src) {
    el.remove()
    return
  }

  let absolute: string

  try {
    absolute = new URL(src, document.baseURI).href
  }
  catch {
    el.remove()
    return
  }

  if (!isSafeMarkingImageSrc(absolute)) {
    el.remove()
    return
  }

  el.setAttribute('src', absolute)
}

function filterClassAttribute(el: Element): void {
  const raw = el.getAttribute('class')

  if (!raw) {
    return
  }

  const kept = filterSemanticMarkingClasses(raw)

  if (kept.length === 0) {
    el.removeAttribute('class')
  }
  else {
    el.setAttribute('class', kept.join(' '))
  }
}

function unwrapElement(el: Element): void {
  const parent = el.parentNode

  if (!parent) {
    return
  }

  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el)
  }

  parent.removeChild(el)
}

/**
 * Interactive elements can't be stored (an anchor would nest inside the
 * marking card's own link, and `button` is not a stored tag), but their text
 * should still read as a reference — swap them for
 * `<span class="marking-link">`, which the excerpt underlines.
 */
function replaceWithLinkSpan(el: Element): void {
  const span = el.ownerDocument!.createElement('span')
  span.setAttribute('class', 'marking-link')

  while (el.firstChild) {
    span.appendChild(el.firstChild)
  }

  el.replaceWith(span)
}

function sanitizeFragment(fragment: DocumentFragment): void {
  // Pre-captured child list so removals/unwraps don't disturb iteration.
  function visit(node: Node): void {
    const children = Array.from(node.childNodes)

    for (const child of children) {
      visit(child)
    }

    if (!(node instanceof Element)) {
      return
    }

    const el = node
    const tag = el.tagName.toLowerCase()

    // Before the `button` tag rule below: the law reference button is content.
    if (isInlineLawReference(el)) {
      replaceWithLinkSpan(el)
      return
    }

    if (REMOVED_TAGS.has(tag) || isExcludedFromMarkings(el)) {
      el.remove()
      return
    }

    if (UNWRAP_TAGS.has(tag)) {
      unwrapElement(el)
      return
    }

    if (tag === 'a') {
      replaceWithLinkSpan(el)
      return
    }

    for (const attr of Array.from(el.attributes)) {
      if (isStrippableAttr(tag, attr.name)) {
        el.removeAttribute(attr.name)
      }
    }

    filterClassAttribute(el)

    if (tag === 'img') {
      sanitizeImage(el)
    }
  }

  visit(fragment)
}

// Ancestors worth reconstructing around a fragment. Generic wrappers (div/
// section/span) are only cloned when they carry a semantic class.
const ANCESTOR_CONTEXT_TAGS = new Set([
  'p',
  'li',
  'ul',
  'ol',
  'dl',
  'dt',
  'dd',
  'blockquote',
  'pre',
  'figure',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'label',
])

/**
 * `Range.cloneContents()` only includes element shells the range PARTIALLY
 * covers — a selection entirely inside one block comes out as bare inline
 * content and loses its formatting. Rebuild by wrapping the fragment in
 * empty clones of the range's ancestors up to the citation parent.
 */
function wrapWithAncestorContext(fragment: DocumentFragment, range: Range): DocumentFragment {
  const common = range.commonAncestorContainer
  let el: Element | null = common instanceof Element ? common : common.parentElement

  let current = fragment

  while (el && el !== document.body && !el.hasAttribute('data-citation-source')) {
    const tag = el.tagName.toLowerCase()
    const keptClasses = filterSemanticMarkingClasses(el.getAttribute('class') ?? '')
    const shouldClone = STORED_TAGS.has(tag)
      && !UNWRAP_TAGS.has(tag)
      && !REMOVED_TAGS.has(tag)
      && !isExcludedFromMarkings(el)
      && (ANCESTOR_CONTEXT_TAGS.has(tag) || keptClasses.length > 0)

    if (shouldClone) {
      const clone = document.createElement(tag)

      if (keptClasses.length > 0) {
        clone.setAttribute('class', keptClasses.join(' '))
      }

      clone.appendChild(current)
      const next = document.createDocumentFragment()
      next.appendChild(clone)
      current = next
    }

    el = el.parentElement
  }

  return current
}

/**
 * Wraps each non-empty text node in `<mark>` so the highlight hugs the text
 * line-by-line instead of painting full-width blocks. Runs AFTER sanitize so
 * originally-present `<mark>`s are already unwrapped and never double-wrap.
 */
function wrapTextNodesInMark(fragment: DocumentFragment): void {
  const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  let n: Node | null = walker.nextNode()

  while (n) {
    if (n instanceof Text) {
      nodes.push(n)
    }

    n = walker.nextNode()
  }

  for (const textNode of nodes) {
    // Whitespace between blocks would render an empty flash of color.
    if (!textNode.data.trim()) {
      continue
    }

    const mark = fragment.ownerDocument!.createElement('mark')
    textNode.parentNode?.replaceChild(mark, textNode)
    mark.appendChild(textNode)
  }
}



/**
 * Sanitized HTML of `range`, or '' on failure/over-cap — callers then fall
 * back to plain `marking.text`.
 */
export function serializeMarkingHtml(range: Range): string {
  try {
    let fragment = range.cloneContents()
    sanitizeFragment(fragment)
    removeEmptySups(fragment)
    fragment = wrapWithAncestorContext(fragment, range)
    wrapTextNodesInMark(fragment)
    const container = document.createElement('div')
    container.appendChild(fragment)
    const html = container.innerHTML.trim()
    return html.length > MAX_STORED_MARKING_HTML_LENGTH ? '' : html
  }
  catch {
    return ''
  }
}
